import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  TextField
} from '@mui/material';
import PhotoGalleryModal from './PhotoGalleryModal';
import MapComponent from './MapComponent';
import ShareMenu from './ShareMenu';
import { buildPropertyUrl } from '../hooks/useShareableUrl';
import { supabase } from '../services/supabaseService';
import { messagesService } from '../services/supabaseService';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  SquareFoot as AreaIcon,
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  Elevator as ElevatorIcon,
  Pets as PetsIcon,
  Balcony as BalconyIcon,
  Kitchen as KitchenIcon,
  Tv as TvIcon,
  AcUnit as AcIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Home as HomeIcon,
  MeetingRoom as RoomIcon,
  DriveEta as CarIcon,
  Euro as EuroIcon,
  Garage as GarageIcon,
  Warehouse as PiwnicaIcon,
  Language as InternetIcon,
  Yard as YardIcon,
  Roofing as RoofingIcon,
  Foundation as ParterIcon,
  LocalLaundryService as PralkaIcon,
  Microwave as MicrowaveIcon,
  CheckCircleOutline as CheckIcon,
  Visibility as VisibilityIcon,
  Email as MessageIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const PropertyDetailsModal = ({ open, onClose, property, onPublish, isSubmitting, progress, isEditMode = false, publishSuccess = false, buttonText = null, userId, userEmail, isFavorite: isFavoriteProp, onFavoritesChange, onLoginRequired, isOwner, hideMessageForm, onGoToMyListings, onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    setIsFav(!!isFavoriteProp);
  }, [isFavoriteProp, open]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [showPhone, setShowPhone] = useState(new Set());
  const [messagesCount, setMessagesCount] = useState(0);

  // Reset message state when modal opens/closes
  useEffect(() => {
    if (open) {
      setMessage(property ? 'Chętnie poznam więcej szczegółów na temat tego ogłoszenia lub umówię się na spotkanie. Proszę o kontakt.' : '');
      setMessageSent(false);
      setMessageSending(false);
      // Fetch messages count for this property
      if (property && property.id && isOwner) {
        supabase.from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('property_id', property.id)
          .then(({ count }) => setMessagesCount(count || 0));
      }
    }
  }, [open, property, isOwner]);

  if (!property) return null;

  // Wysyłanie wiadomości do ogłoszeniodawcy
  const handleSendMessage = async () => {
    if (!message.trim() || messageSending) return;
    if (!userId) {
      // Użytkownik niezalogowany — wymagaj logowania
      if (onLoginRequired) onLoginRequired('message');
      return;
    }
    setMessageSending(true);
    try {
      const sendPayload = {
        propertyId: property.id !== 'preview' ? property.id : null,
        recipientEmail: safePropertyData.contact.email,
        recipientName: safePropertyData.contact.name,
        senderUserId: userId,
        senderEmail: userEmail || '',
        messageText: message.trim(),
        listingTitle: propertyData.title || `Ogłoszenie #${property.id}`
      };
      await messagesService.send(sendPayload);
      setMessageSent(true);
      setMessage('');
      setTimeout(() => {
        setMessageSent(false);
        setMessage(property ? 'Chętnie poznam więcej szczegółów na temat tego ogłoszenia lub umówię się na spotkanie. Proszę o kontakt.' : '');
      }, 4000);
    } catch (err) {
      console.error('Błąd wysyłania wiadomości:', err);
      alert('Nie udało się wysłać wiadomości. Spróbuj ponownie.');
    } finally {
      setMessageSending(false);
    }
  };


  // Use real images from property.image_url (Supabase)
  const propertyData = property;
  // Fallback if no property (should not happen)
  if (!propertyData) return null;

  // Get images array from propertyData.image_url
  const images = Array.isArray(propertyData.image_url)
    ? propertyData.image_url.filter(url => url && url.trim() !== '')
    : propertyData.image_url ? [propertyData.image_url] : [];

  // Create safe property data with defaults
  const safePropertyData = {
    ...propertyData,
    images,
    rooms: propertyData.rooms != null ? propertyData.rooms : 2,
    area: propertyData.area || 50,
    floor: propertyData.floor || 1,
    // Użyj aktualnej daty dla podglądu (gdy id='preview') lub istniejacych dat
    updated: propertyData.id === 'preview' 
      ? new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : (propertyData.updated || null),
    published: propertyData.id === 'preview'
      ? new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : (propertyData.published || new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })),
    type: propertyData.type || "Mieszkanie",
    amenities: propertyData.amenities || [],
    contact: propertyData.contact || { name: propertyData.first_name || '', phone: propertyData.phone || '', email: propertyData.email || '' },
    price: typeof propertyData.price === 'string' 
      ? parseInt(propertyData.price.replace(/[^\d]/g, '')) 
      : propertyData.price || 0
  };

  // Helper to format price
  const formatPrice = (value) =>
    typeof value === 'number' ? `${value.toLocaleString('pl-PL')} zł/mies.` : `${value} zł/mies.`;

  // Calculate total price (czynsz + wynajem)
  const czynsz = propertyData.rent ? Number(propertyData.rent) : 0;
  const wynajem = propertyData.price ? Number(propertyData.price) : 0;
  const cenaCalkowita = czynsz + wynajem;

  const amenityIcons = {
    'WiFi': WifiIcon,
    'Parking': ParkingIcon,
    'Winda': ElevatorIcon,
    'Zwierzęta': PetsIcon,
    'Balkon': BalconyIcon,
    'Klimatyzacja': AcIcon,
    'TV': TvIcon,
    'Telewizor': TvIcon,
    'Zmywarka': KitchenIcon,
    'Garaż': GarageIcon,
    'Piwnica': PiwnicaIcon,
    'Internet': InternetIcon,
    'Ogródek': YardIcon,
    'Ostatnie piętro': RoofingIcon,
    'Parter': ParterIcon,
    'Pralka': PralkaIcon,
    'Piekarnik': KitchenIcon,
    'Kuchenka mikrofalowa': MicrowaveIcon,
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const toggleFavorite = async () => {
    if (!userId) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    try {
      const { data } = await supabase.from('users').select('favorites').eq('id', userId).single();
      const currentFavs = (data?.favorites || []).map(Number);
      const propId = Number(property.id);
      const newFavs = currentFavs.includes(propId)
        ? currentFavs.filter(id => id !== propId)
        : [...currentFavs, propId];
      await supabase.from('users').update({ favorites: newFavs }).eq('id', userId);
      setIsFav(newFavs.includes(propId));
      if (onFavoritesChange) onFavoritesChange();
    } catch (err) {
    }
  };

  const handlePhoneReveal = async (propId) => {
    if (showPhone.has(propId)) return;
    // Wymagaj zalogowania — niezalogowany widzi modal logowania/rejestracji
    if (!userId) {
      if (onLoginRequired) onLoginRequired('phone');
      return;
    }
    // Sprawdź localStorage
    const stored = JSON.parse(localStorage.getItem('phone_reveals') || '[]');
    if (stored.includes(propId)) {
      setShowPhone(prev => new Set([...prev, propId]));
      return;
    }
    // Sprawdź w Supabase czy już odkrywał
    const { data } = await supabase.from('phone_reveal_events')
      .select('id').eq('property_id', propId).eq('user_id', userId).limit(1);
    if (data && data.length > 0) {
      stored.push(propId);
      localStorage.setItem('phone_reveals', JSON.stringify(stored));
      setShowPhone(prev => new Set([...prev, propId]));
      return;
    }
    // Nowe odkrycie — zapisz event i inkrementuj licznik
    try {
      await supabase.from('phone_reveal_events').insert({ property_id: propId, user_id: userId });
      await supabase.rpc('increment_phone_reveals', { prop_id: propId }).then(({ error }) => {
        if (error) {
          supabase.from('properties').update({ phone_reveals: (property.phone_reveals || 0) + 1 }).eq('id', propId);
        }
      });
    } catch (e) {}
    stored.push(propId);
    localStorage.setItem('phone_reveals', JSON.stringify(stored));
    setShowPhone(prev => new Set([...prev, propId]));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      disableScrollLock={true}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          m: isMobile ? 0 : 2
        }
      }}
    >
      <DialogContent sx={{ 
        p: 0, 
        position: 'relative',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px'
        }
      }}>
        {/* Close Button */}
        <IconButton 
          onClick={onClose} 
          sx={{ 
            position: 'absolute',
            top: isMobile ? 8 : 16,
            right: isMobile ? 8 : 16, // Z powrotem po prawej stronie
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            width: 40, // Oryginalny rozmiar
            height: 40,
            '&:hover': {
              backgroundColor: 'white'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>

        <Box sx={{ 
          minHeight: '100vh', 
          overflow: 'auto',
          // Równomierne marginesy po obu stronach
          mx: { xs: 1, md: 2 }
        }}>
          {/* Image Gallery Section - Full Width */}
          <Box sx={{ 
            height: isMobile ? '300px' : '400px', 
            position: 'relative',
            mb: 3,
            px: { xs: 1, md: 2 },
            pt: 2,
            pb: 0
          }}>
            <Box sx={{ 
              display: 'flex',
              gap: 2,
              height: '100%'
            }}>
              {/* Main Image - Left Side */}
              <Box sx={{
                flex: isMobile ? 1 : '0 0 66.67%',
                height: '100%',
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden'
              }}>
                <img 
                  src={images[selectedImageIndex]}
                  alt="Property"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&h=600&fit=crop&auto=format';
                  }}
                />
                {/* ID Badge - ukryte na mobile */}
                <Box sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  left: 16,
                  display: { xs: 'none', md: 'block' } // Ukryj na mobile
                }}>
                  <Chip 
                    label="ID 64048" 
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                
                {/* Favorite and Share Icons */}
                <Box sx={{ 
                  position: 'absolute', 
                  top: isMobile ? 8 : 16, 
                  left: isMobile ? 8 : 16, // Przenieś na lewą stronę
                  display: 'flex',
                  gap: 1
                }}>
                  <IconButton 
                    onClick={toggleFavorite}
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      width: 40,
                      height: 40,
                      '&:hover': { backgroundColor: 'white' }
                    }}
                  >
                    {isFav ? 
                      <FavoriteIcon sx={{ color: '#ff4444', fontSize: 24 }} /> : 
                      <FavoriteBorderIcon sx={{ fontSize: 24 }} />
                    }
                  </IconButton>
                  <ShareMenu
                    url={`${window.location.origin}${buildPropertyUrl(property)}`}
                    title={property?.title || 'Ogłoszenie'}
                    text={property?.title || 'Zobacz ogłoszenie'}
                    iconButtonSx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', width: 40, height: 40, '&:hover': { backgroundColor: 'white' } }}
                    iconSx={{ fontSize: 24 }}
                  />
                </Box>

                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={handlePrevImage}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleNextImage}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </>
                )}

                {/* View All Photos Button - Only on main image */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 16, 
                  right: 16
                }}>
                  <Button 
                    variant="contained" 
                    size="medium"
                    onClick={() => setGalleryOpen(true)}
                    sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      borderRadius: 3,
                      px: 2,
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
                    }}
                  >
                    📷 Zobacz wszystkie ({images.length})
                  </Button>
                </Box>
              </Box>

              {/* Thumbnail Images - Right Side ONLY on Desktop */}
              {!isMobile && images.length > 1 && (
                <Box sx={{
                  flex: '0 0 33.33%',
                  height: '100%',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridAutoRows: '1fr',
                  gap: 1,
                  alignContent: 'start',
                  overflow: 'hidden'
                }}>
                  {images.slice(1, 5).map((image, index) => (
                    <Box
                      key={index + 1}
                      onClick={() => handleImageClick(index + 1)}
                      sx={{
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: selectedImageIndex === index + 1 ? '3px solid #D2691E' : '3px solid transparent',
                        overflow: 'hidden',
                        aspectRatio: '1 / 1',
                        '&:hover': {
                          opacity: 0.8,
                          transform: 'scale(0.98)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <img 
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.src = `https://picsum.photos/400/300?random=${index + 1}`;
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Title and Location Section */}
          <Box sx={{ px: { xs: 1, md: 2 }, pb: 2 }}>
            {/* Status Badges */}
            <Box sx={{ mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip 
                label={`Opublikowano: ${safePropertyData.published}`}
                icon={<Box sx={{ fontSize: '12px', color: '#999' }}>●</Box>}
                size="small"
                variant="outlined"
                sx={{ color: '#666', borderColor: '#ccc', fontSize: { xs: '11px', md: '13px' }, height: { xs: 26, md: 32 } }}
              />

              <Chip 
                label={propertyData.available_from ? `Dostępne od: ${propertyData.available_from}` : 'Dostępne od zaraz'}
                size="small"
                sx={{ 
                  backgroundColor: '#E8F5E8', 
                  color: '#2E7D2E',
                  fontWeight: '600',
                  fontSize: { xs: '11px', md: '13px' },
                  height: { xs: 26, md: 32 }
                }}
              />
            </Box>

            {/* Title */}
            <Typography variant="h4" sx={{ 
              fontWeight: '600', 
              mb: 1.5,
              fontSize: { xs: '18px', md: '28px' },
              lineHeight: 1.3
            }}>
              {propertyData.title || 'Mieszkanie • Sprzedaż / Wynajem miesięczny / Wynajem dobowy'}
            </Typography>

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ color: '#EE2005', mr: 0.5, fontSize: { xs: 20, md: 24 } }} />
              <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '13px', md: '15px' } }}>
                {[propertyData.city, propertyData.street?.trim(), propertyData.district?.trim()].filter(Boolean).join(', ')
                  || safePropertyData.address || `${safePropertyData.location?.address || ''}, ${safePropertyData.location?.city || ''}`}
              </Typography>
            </Box>

            {/* Property Details and Amenities Icons */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: 'repeat(2, auto)', md: 'repeat(4, auto)' },
              gap: { xs: 0.5, md: 1 }, 
              mb: 3,
              justifyContent: 'start',
              columnGap: { xs: 2, md: 3 }
            }}>
              {/* Basic info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RoomIcon sx={{ color: '#666', fontSize: { xs: 16, md: 20 } }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '14px' } }}>
                  {safePropertyData.rooms === 0 ? 'Pokój w mieszkaniu' : safePropertyData.rooms === 1 ? 'Kawalerka' : `${safePropertyData.rooms} ${safePropertyData.rooms <= 4 ? 'pokoje' : 'pokoi'}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AreaIcon sx={{ color: '#666', fontSize: { xs: 16, md: 20 } }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '14px' } }}>
                  {safePropertyData.area} m²
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HomeIcon sx={{ color: '#666', fontSize: { xs: 16, md: 20 } }} />
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '14px' } }}>
                  {safePropertyData.floor} piętro
                </Typography>
              </Box>
              
              {/* Amenities as small icons */}
              {(safePropertyData.amenities || []).map((amenity, index) => {
                const IconComponent = amenityIcons[amenity] || CheckIcon;
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconComponent sx={{ color: '#666', fontSize: { xs: 16, md: 20 } }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', md: '14px' } }}>
                      {amenity}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Content Section */}
          <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
            {/* Price Section + Message Form on Desktop */}
            <Box sx={{ 
              display: { xs: 'block', md: 'flex' }, 
              gap: 3, 
              mb: 3,
              alignItems: 'flex-start',
              overflow: 'hidden'
            }}>
              {/* Price Section */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Paper elevation={0} sx={{ 
                  p: { xs: 2, md: 3 }, 
                  backgroundColor: '#F8F9FA',
                  border: '1px solid #E9ECEF'
                }}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, auto)' },
                    gap: { xs: 2, md: 4 },
                    justifyContent: { md: 'flex-start' }
                  }}>
                    {/* Wynajem */}
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5, fontSize: { xs: '12px', md: '14px' } }}>
                        Wynajem
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        color: '#666', 
                        fontWeight: '600',
                        fontSize: { xs: '14px', md: '18px' }
                      }}>
                        {formatPrice(wynajem)}
                      </Typography>
                    </Box>

                    {/* Czynsz */}
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5, fontSize: { xs: '12px', md: '14px' } }}>
                        Czynsz
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        color: '#666', 
                        fontWeight: '600',
                        fontSize: { xs: '14px', md: '18px' }
                      }}>
                        {formatPrice(czynsz)}
                      </Typography>
                    </Box>

                    {/* Cena całkowita */}
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5, fontSize: { xs: '12px', md: '14px' } }}>
                        Cena całkowita
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        color: '#28A745', 
                        fontWeight: '600',
                        fontSize: { xs: '14px', md: '18px' }
                      }}>
                        {formatPrice(cenaCalkowita)}
                      </Typography>
                      {propertyData.previous_price && (
                        <Typography sx={{ 
                          color: '#E57373', 
                          fontWeight: '600',
                          fontSize: { xs: '13px', md: '14px' },
                          textDecoration: 'line-through',
                          lineHeight: 1.2
                        }}>
                          {(() => {
                            const prevPrice = Number(propertyData.previous_price) || 0;
                            const prevTotal = prevPrice + czynsz;
                            return `${prevTotal.toLocaleString('pl-PL')} zł/mies.`;
                          })()}
                        </Typography>
                      )}
                    </Box>

                    {/* Kaucja */}
                    <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5, fontSize: { xs: '12px', md: '14px' } }}>
                        Wysokość kaucji
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        color: '#666', 
                        fontWeight: '600',
                        fontSize: { xs: '14px', md: '18px' }
                      }}>
                        {propertyData.deposit ? `${Number(propertyData.deposit).toLocaleString('pl-PL')} zł` : '0 zł'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Wyposażenie */}
                {safePropertyData.equipment && safePropertyData.equipment.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: '600', fontSize: { xs: '15px', md: '18px' } }}>
                      Wyposażenie
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
                      {safePropertyData.equipment.map((item, index) => (
                        <Box component="li" key={index} sx={{ mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Description */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: '600', fontSize: { xs: '15px', md: '18px' } }}>
                    Opis
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#666', 
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line',
                    fontSize: { xs: '13px', md: '16px' }
                  }}>
                    {safePropertyData.description}
                  </Typography>
                </Box>

                {/* Action Buttons - Desktop Only, pod opisem w trybie podglądu */}
                {!isMobile && onPublish && (
                  <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Back to Edit Button */}
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={onClose}
                      sx={{
                        borderColor: '#FF8C00',
                        color: '#FF8C00',
                        py: 1.5,
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#E67E00',
                          backgroundColor: '#FFF7ED',
                          color: '#E67E00'
                        }
                      }}
                    >
                      Powrót do edycji
                    </Button>
                    
                    {/* Publish Button */}
                    <Box sx={{ position: 'relative' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isSubmitting}
                        onClick={() => onPublish && onPublish()}
                        sx={{
                          backgroundColor: '#111',
                          color: 'white',
                          py: 2,
                          fontSize: '16px',
                          fontWeight: '600',
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#333'
                          }
                        }}
                      >
                        {isSubmitting ? (isEditMode ? 'Zapisywanie...' : 'Publikowanie...') : (isEditMode ? 'Zapisz zmiany' : 'Opublikuj ogłoszenie')}
                      </Button>
                      
                      {isSubmitting && (
                        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                          <Box sx={{ width: '100%', height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
                            <Box sx={{ width: `${progress || 0}%`, height: 4, backgroundColor: '#D2691E', borderRadius: 2, transition: 'width 0.1s' }} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Action Buttons - Mobile Only, pod opisem w trybie podglądu */}
                {isMobile && onPublish && (
                  <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Back to Edit Button */}
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={onClose}
                      sx={{
                        borderColor: '#FF8C00',
                        color: '#FF8C00',
                        py: 1.5,
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#E67E00',
                          backgroundColor: '#FFF7ED',
                          color: '#E67E00'
                        }
                      }}
                    >
                      Powrót do edycji
                    </Button>
                    
                    {/* Publish Button */}
                    <Box sx={{ position: 'relative' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isSubmitting || publishSuccess}
                        onClick={() => onPublish && onPublish()}
                        sx={{
                          backgroundColor: publishSuccess ? '#2e7d32' : '#111',
                          color: 'white',
                          py: 2,
                          fontSize: '16px',
                          fontWeight: '600',
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: publishSuccess ? '#2e7d32' : '#333'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: publishSuccess ? '#2e7d32' : undefined,
                            color: 'white'
                          }
                        }}
                      >
                        {publishSuccess
                          ? (buttonText || (isEditMode ? '✓ Zmiany zapisane' : '✓ Ogłoszenie dodane'))
                          : isSubmitting
                            ? (isEditMode ? 'Zapisywanie...' : 'Publikowanie...')
                            : (isEditMode ? 'Zapisz zmiany' : 'Opublikuj ogłoszenie')}
                      </Button>
                      
                      {isSubmitting && (
                        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                          <Box sx={{ width: '100%', height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
                            <Box sx={{ width: `${progress || 0}%`, height: 4, backgroundColor: '#D2691E', borderRadius: 2, transition: 'width 0.1s' }} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Map - Mobile Only lub Desktop bez trybu podglądu */}
                {(isMobile || !onPublish) && (
                  <Box sx={{ mt: 3 }}>
                    <MapComponent 
                      address={[
                        propertyData.street,
                        propertyData.building_number,
                        propertyData.district,
                        propertyData.city
                      ].filter(Boolean).join(', ')}
                      lat={propertyData.lat || null}
                      lng={propertyData.lng || null}
                      title="Lokalizacja na mapie"
                    />
                  </Box>
                )}
              </Box>

              {/* Message Form - Desktop Only (ukryj w trybie podglądu) */}
              {!isMobile && !onPublish && !isOwner && !hideMessageForm && (
                <Box sx={{ flex: '0 0 300px', maxWidth: '35%', minWidth: 0 }}>
                  <Paper elevation={2} sx={{ 
                    p: 3, 
                    height: 'fit-content'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', fontSize: '1.1rem' }}>
                      Napisz do właściciela
                    </Typography>
                    
                    <Typography variant="body2" sx={{ 
                      mb: 2,
                      color: '#666'
                    }}>
                      {safePropertyData.contact.name}
                    </Typography>

                    {messageSent ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CheckIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: '600', color: '#4CAF50', mb: 2 }}>
                          Wiadomość została wysłana!
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <TextField
                          multiline
                          rows={4}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          fullWidth
                          disabled={messageSending}
                          error={!message.trim()}
                          helperText={!message.trim() ? 'Wiadomość nie może być pusta' : ''}
                          sx={{ mb: 2 }}
                        />

                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleSendMessage}
                          disabled={!message.trim() || messageSending}
                          sx={{
                            backgroundColor: '#D2691E',
                            '&:hover': {
                              backgroundColor: '#B8591A'
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#ccc'
                            },
                            mb: 2
                          }}
                        >
                          {messageSending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                        </Button>
                      </>
                    )}

                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PhoneIcon />}
                      onClick={() => handlePhoneReveal(property.id)}
                      sx={{
                        borderColor: '#D2691E',
                        color: '#D2691E',
                        cursor: showPhone.has(property.id) ? 'default' : 'pointer',
                        '&:hover': {
                          borderColor: '#B8591A',
                          backgroundColor: showPhone.has(property.id) ? 'transparent' : 'rgba(210, 105, 30, 0.08)'
                        }
                      }}
                    >
                      {showPhone.has(property.id) ? safePropertyData.contact.phone : 'Pokaż telefon'}
                    </Button>

                    <Box sx={{ 
                      mt: 3,
                      pt: 2,
                      borderTop: '1px solid #E9ECEF'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: { xs: 'none', md: 'block' }, // Ukryj na mobile
                        textAlign: 'center',
                        color: '#666',
                        mb: 1
                      }}>
                        ID ogłoszenia: {property.id}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}

              {/* Own listing button - Desktop (from carousel) */}
              {!isMobile && !onPublish && hideMessageForm && onGoToMyListings && (
                <Box sx={{ flex: '0 0 300px', maxWidth: '35%', minWidth: 0 }}>
                  <Paper elevation={2} sx={{ p: 3, height: 'fit-content', textAlign: 'center' }}>
                    <HomeIcon sx={{ fontSize: 48, color: '#D2691E', mb: 1 }} />
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: '600', color: '#555' }}>
                      To jest Twoje ogłoszenie
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<EditIcon />}
                      onClick={onGoToMyListings}
                      sx={{
                        backgroundColor: '#D2691E',
                        '&:hover': { backgroundColor: '#A0522D' },
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        py: 1.2
                      }}
                    >
                      Moje ogłoszenia
                    </Button>
                  </Paper>
                </Box>
              )}

              {/* Owner Stats Panel - Desktop */}
              {!isMobile && !onPublish && isOwner && (
                <Box sx={{ flex: '0 0 300px', maxWidth: '35%', minWidth: 0 }}>
                  <Paper elevation={2} sx={{ p: 3, height: 'fit-content' }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: '600', fontSize: '1.1rem' }}>
                      Statystyki ogłoszenia
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <VisibilityIcon sx={{ color: '#666', fontSize: 22 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Wyświetlenia</Typography>
                        <Typography variant="body1" sx={{ fontWeight: '600' }}>{property.views || 0}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <MessageIcon sx={{ color: '#666', fontSize: 22 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Wysłane wiadomości</Typography>
                        <Typography variant="body1" sx={{ fontWeight: '600' }}>{messagesCount}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <PhoneIcon sx={{ color: '#666', fontSize: 22 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Odkrycia telefonu</Typography>
                        <Typography variant="body1" sx={{ fontWeight: '600' }}>{property.phone_reveals || 0}</Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<EditIcon />}
                      onClick={() => onEdit && onEdit(property)}
                      sx={{
                        backgroundColor: '#FB923C',
                        '&:hover': { backgroundColor: '#EA580C' },
                        textTransform: 'none',
                        py: 1.5,
                        fontWeight: '600'
                      }}
                    >
                      Edytuj ogłoszenie
                    </Button>
                  </Paper>
                </Box>
              )}
              
              {!isMobile && onPublish && (
                <Box sx={{ flex: '0 0 300px', maxWidth: '35%', minWidth: 0 }}>
                  <Box sx={{ position: 'sticky', top: 20 }}>
                    <MapComponent 
                      address={[
                        propertyData.street,
                        propertyData.building_number,
                        propertyData.district,
                        propertyData.city
                      ].filter(Boolean).join(', ')}
                      lat={propertyData.lat || null}
                      lng={propertyData.lng || null}
                      title="Lokalizacja na mapie"
                    />
                  </Box>
                </Box>
              )}
            </Box>



            {/* Message Form - Mobile Only (ukryj w trybie podglądu) */}
            {isMobile && !onPublish && !isOwner && !hideMessageForm && (
              <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
                <Paper elevation={2} sx={{ 
                  p: 3, 
                  mb: 2
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: '600' }}>
                    Napisz do właściciela
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    color: '#666'
                  }}>
                    {safePropertyData.contact.name}
                  </Typography>

                  {messageSent ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <CheckIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: '600', color: '#4CAF50', mb: 2 }}>
                        Wiadomość została wysłana!
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TextField
                        multiline
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        fullWidth
                        disabled={messageSending}
                        error={!message.trim()}
                        helperText={!message.trim() ? 'Wiadomość nie może być pusta' : ''}
                        sx={{ mb: 2 }}
                      />

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSendMessage}
                        disabled={!message.trim() || messageSending}
                        sx={{
                          backgroundColor: '#D2691E',
                          '&:hover': {
                            backgroundColor: '#B8591A'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#ccc'
                          },
                          mb: 2
                        }}
                      >
                        {messageSending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PhoneIcon />}
                    onClick={() => handlePhoneReveal(property.id)}
                    sx={{
                      borderColor: '#D2691E',
                      color: '#D2691E',
                      cursor: showPhone.has(property.id) ? 'default' : 'pointer',
                      '&:hover': {
                        borderColor: '#B8591A',
                        backgroundColor: showPhone.has(property.id) ? 'transparent' : 'rgba(210, 105, 30, 0.08)'
                      }
                    }}
                  >
                    {showPhone.has(property.id) ? safePropertyData.contact.phone : 'Pokaż telefon'}
                  </Button>

                  <Box sx={{ 
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #E9ECEF'
                  }}>
                    <Typography variant="caption" sx={{ 
                      display: { xs: 'none', md: 'block' }, // Ukryj na mobile
                      textAlign: 'center',
                      color: '#666',
                      mb: 1
                    }}>
                      ID ogłoszenia: {property.id}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Own listing button - Mobile (from carousel) */}
            {isMobile && !onPublish && hideMessageForm && onGoToMyListings && (
              <Box sx={{ px: 1, pb: 3 }}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                  <HomeIcon sx={{ fontSize: 48, color: '#D2691E', mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: '600', color: '#555' }}>
                    To jest Twoje ogłoszenie
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<EditIcon />}
                    onClick={onGoToMyListings}
                    sx={{
                      backgroundColor: '#D2691E',
                      '&:hover': { backgroundColor: '#A0522D' },
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: 2,
                      py: 1.2
                    }}
                  >
                    Moje ogłoszenia
                  </Button>
                </Paper>
              </Box>
            )}

            {/* Owner Stats - Mobile */}
            {isMobile && !onPublish && isOwner && (
              <Box sx={{ px: 1, pb: 3 }}>
                <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: '600' }}>
                    Statystyki ogłoszenia
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <VisibilityIcon sx={{ color: '#666', fontSize: 22 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Wyświetlenia</Typography>
                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{property.views || 0}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <MessageIcon sx={{ color: '#666', fontSize: 22 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Wysłane wiadomości</Typography>
                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{messagesCount}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <PhoneIcon sx={{ color: '#666', fontSize: 22 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Odkrycia telefonu</Typography>
                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{property.phone_reveals || 0}</Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<EditIcon />}
                    onClick={() => onEdit && onEdit(property)}
                    sx={{
                      backgroundColor: '#FB923C',
                      '&:hover': { backgroundColor: '#EA580C' },
                      textTransform: 'none',
                      py: 1.5,
                      fontWeight: '600'
                    }}
                  >
                    Edytuj ogłoszenie
                  </Button>
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={images}
        title={propertyData.title}
      />
    </Dialog>
  );
};

export default PropertyDetailsModal;